import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

const DropdownMultiselectDark = ({
  options = [],
  selectedValues = [],
  onSelect = () => {},
  showAllOption = false,
  allOptionText = 'All',
  allOptionValue = '',
  placeholder = 'Select',
  width,
  dropdownClassMenu = 'bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5',
  dropdownClassOption = 'cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-600 text-white flex items-center',
  dropdownClassEmpty = 'px-3 py-1.5 text-xs text-gray-300 italic',
  dropdownClassContainer = '',
  dropdownClassName = '',
  usePortal = false,
  portalRef = null,
  gridCols = 'grid-cols-2 md:grid-cols-3'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelected, setInternalSelected] = useState(selectedValues);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    setInternalSelected(selectedValues);
  }, [selectedValues, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (buttonRef.current && buttonRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value) => {
    let newValues;
    if (internalSelected.includes(value)) {
      newValues = internalSelected.filter((v) => v !== value);
    } else {
      newValues = [...internalSelected, value];
    }
    setInternalSelected(newValues);
  };

  const handleClearAll = () => {
    setInternalSelected([]);
  };

  const handleApply = () => {
    onSelect(internalSelected);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    const allValues = options.map(opt => typeof opt === 'object' ? opt.value : opt);
    setInternalSelected(allValues);
  };

  // Função utilitária para obter o índice do mês anterior e subsequente
  const getMonthIndices = () => {
    // Extrai os valores dos meses selecionados e converte para índice
    const optionValues = options.map(opt => typeof opt === 'object' ? opt.value : opt);
    const selectedIndices = internalSelected
      .map(val => optionValues.indexOf(val))
      .filter(idx => idx !== -1)
      .sort((a, b) => a - b);
    return { optionValues, selectedIndices };
  };

  // Handler para o botão '<'
  const handlePrev = () => {
    const { optionValues, selectedIndices } = getMonthIndices();
    if (selectedIndices.length === 0) return;
    const minIdx = selectedIndices[0];
    if (minIdx > 0) {
      const prevValue = optionValues[minIdx - 1];
      setInternalSelected([prevValue]);
      onSelect([prevValue]);
    }
  };

  // Handler para o botão '>'
  const handleNext = () => {
    const { optionValues, selectedIndices } = getMonthIndices();
    if (selectedIndices.length === 0) return;
    const maxIdx = selectedIndices[selectedIndices.length - 1];
    if (maxIdx < optionValues.length - 1) {
      const nextValue = optionValues[maxIdx + 1];
      setInternalSelected([nextValue]);
      onSelect([nextValue]);
    }
  };

  // Desabilitar botões quando não houver anterior/subsequente
  const { selectedIndices, optionValues } = getMonthIndices();
  const disablePrev = selectedIndices.length === 0 || selectedIndices[0] === 0;
  const disableNext = selectedIndices.length === 0 || selectedIndices[selectedIndices.length - 1] === optionValues.length - 1;

  // Portal positioning logic
  const getPortalStyle = () => {
    const ref = portalRef && portalRef.current ? portalRef : buttonRef;
    if (!ref.current) return {};
    const rect = ref.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 8,
      minWidth: rect.width,
    };
  };

  const menuContent = (
    <div
      ref={dropdownRef}
      className={`rounded-md ${dropdownClassMenu} ${dropdownClassContainer} ${dropdownClassName}`}
      style={usePortal ? {} : { position: 'absolute', top: '100%', marginTop: '0.25rem', width: width || '100%' }}
    >
      {showAllOption && (
        <div className="mb-2">
          <button
            onClick={() => handleSelect(allOptionValue)}
            className={`w-full py-1.5 text-xs rounded-md mb-1 ${internalSelected.includes(allOptionValue) ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-200 hover:bg-gray-500'}`}
          >
            {allOptionText}
          </button>
        </div>
      )}
      <div className={`grid ${gridCols} gap-2 px-2 pb-2 pt-2`}>
        {options.map((option, index) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          const isSelected = internalSelected.includes(optionValue);
          return (
            <button
              key={index}
              onClick={() => handleSelect(optionValue)}
              className={`py-2 rounded-md text-xs font-medium transition-all border 
                ${isSelected ? 'bg-green-600 text-white border-green-400' : 'bg-gray-600 text-gray-200 border-transparent hover:bg-gray-500'}
                focus:outline-none`}
              style={{ minWidth: 0 }}
            >
              {optionLabel}
            </button>
          );
        })}
      </div>
      {options.length === 0 && (
        <div className={dropdownClassEmpty}>
          No options available
        </div>
      )}
      <div className="flex items-center justify-center gap-2 px-3 py-2 border-t border-gray-600 mt-2">
        <button
          onClick={handleSelectAll}
          className="text-xs text-gray-300 hover:text-green-400 focus:outline-none px-0 py-1"
        >
          Select All
        </button>
        <button
          onClick={handleClearAll}
          className="text-xs text-gray-300 hover:text-red-400 focus:outline-none px-0 py-1"
        >
          Clear All
        </button>
      </div>
      <div className="flex items-center justify-end px-3 pb-2">
        <button
          onClick={handleApply}
          className={`text-xs text-white bg-green-600 hover:bg-green-700 rounded px-3 py-1 font-semibold focus:outline-none ${internalSelected.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
          disabled={internalSelected.length === 0}
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <div className={`relative flex items-center ${width || 'w-full'}`} ref={buttonRef}>
      <button
        type="button"
        onClick={handlePrev}
        className={`flex items-center justify-center h-8 w-8 rounded-l-full rounded-r-none bg-gray-500 bg-opacity-30 text-white hover:bg-gray-600 focus:outline-none border border-gray-600 border-r-0 ${disablePrev ? 'opacity-40 cursor-not-allowed' : ''}`}
        tabIndex={-1}
        style={{ minWidth: 32 }}
        disabled={disablePrev}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      <div className="flex-1">
        <button
          type="button"
          onClick={handleToggle}
          className="inline-flex justify-center items-center w-full h-8 rounded-none border-0 bg-gray-500 bg-opacity-30 text-xs text-white hover:bg-gray-600 focus:outline-none border-t border-b border-gray-600 px-2"
          style={{ borderLeft: 0, borderRight: 0 }}
        >
          <span className="flex-1 text-center truncate">
            {selectedValues.length === 0
              ? placeholder
              : selectedValues.length > 1
                ? '(multiple)'
                : (() => {
                    const val = selectedValues[0];
                    const found = options.find(opt => (typeof opt === 'object' ? opt.value : opt) === val);
                    return found && typeof found === 'object' ? found.label : val;
                  })()
            }
          </span>
          <ChevronDownIcon 
            className={`ml-1 h-4 w-4 text-white opacity-70 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>
        {isOpen && (
          usePortal
            ? createPortal(
                <div style={{...getPortalStyle(), minWidth: buttonRef.current ? buttonRef.current.offsetWidth : undefined, width: buttonRef.current ? buttonRef.current.offsetWidth : undefined}}>{menuContent}</div>,
                document.body
              )
            : React.cloneElement(menuContent, { style: { ...(menuContent.props.style || {}), width: '100%' } })
        )}
      </div>
      <button
        type="button"
        onClick={handleNext}
        className={`flex items-center justify-center h-8 w-8 rounded-r-full rounded-l-none bg-gray-500 bg-opacity-30 text-white hover:bg-gray-600 focus:outline-none border border-gray-600 border-l-0 ${disableNext ? 'opacity-40 cursor-not-allowed' : ''}`}
        tabIndex={-1}
        style={{ minWidth: 32 }}
        disabled={disableNext}
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

export default DropdownMultiselectDark;
